<template>
  <div v-if="availableExaminationActions?.length" class="dropdown">
    <button
      id="dropdownMenuButton"
      :class="['btn', iconOnly ? 'icon-dropdown' : 'btn-custom']"
      type="button"
      data-toggle="dropdown"
      aria-haspopup="true"
      aria-expanded="false"
      :aria-label="trans('general.action')"
      :disabled="loading || !availableExaminationActions"
      :title="iconOnly ? trans('general.action') : ''"
      :style="iconOnly ? { width: '44px' } : {}"
    >
      <span v-if="iconOnly" class="fas fa-ellipsis-v"></span>
      <span v-else>{{ trans("general.action") }}</span>
    </button>
    <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
      <a
        v-for="(item, index) in availableExaminationActions"
        :key="index"
        class="dropdown-item"
        href="#"
        @click.prevent="onAction(item)"
      >
        {{ item.actionTitle }}
      </a>
    </div>
  </div>
</template>

<script>
import { productHasPermission } from "../../../../../helpers/product";
import {
  PRODUCT_ACTIONS,
  PRODUCT_ACTION_NAMES,
  PRODUCT_PERMISSIONS,
} from "../../../../../helpers/definitions";

export default {
  props: {
    examinationActions: Object,
    product: Object,
    loading: Boolean,
    errorUpdatingActions: Boolean,
    caseClosedAt: [String, Object],
    viewOption: String,
    iconOnly: {
      type: Boolean,
      default: false,
    },
  },
  data: function () {
    return {
      productActions: PRODUCT_ACTIONS,
      availableExaminationActions: null,
    };
  },
  watch: {
    errorUpdatingActions: function () {
      if (this.errorUpdatingActions) {
        this.mapCaseActions();
      }
    },
    caseClosedAt: function () {
      this.mapCaseActions();
    },
  },
  mounted: function () {
    this.$nextTick(() => {
      this.mapCaseActions();
    });
  },
  methods: {
    mapCaseActions: function () {
      let enabledActions = [];

      this.productActions.forEach((action) => {
        // CDR flagging is platform-wide and not gated by product permissions or user abilities
        const isCdrFlagCase =
          action.name === PRODUCT_ACTION_NAMES.CDR_REPORT_CASE;
        if (isCdrFlagCase && this.examinationActions?.hasPendingCdrFlag) return;
        let enabled =
          isCdrFlagCase ||
          (productHasPermission(action.name, this.product) &&
            (this.$can(action.name) ||
              action.name === PRODUCT_ACTION_NAMES.UCR_PROMOTION));

        if (enabled) {
          if (
            this.viewOption === "patient_overview_header" &&
            ![
              PRODUCT_ACTION_NAMES.PRINT_CASE,
              PRODUCT_ACTION_NAMES.CLOSE_CASE,
              PRODUCT_ACTION_NAMES.CDR_REPORT_CASE,
            ].includes(action.name)
          ) {
            return;
          }

          // In patient overview, only the CDR flag action is relevant
          if (
            this.viewOption === "patient_overview" &&
            action.name !== PRODUCT_ACTION_NAMES.CDR_REPORT_CASE
          ) {
            return;
          }

          // don't show option to close case if case is already closed or actions are on examination overview
          if (
            action.name === PRODUCT_PERMISSIONS.CLOSE_CASES &&
            (this.caseClosedAt || this.viewOption === "examination_overview")
          ) {
            return;
          }
          // don't show edit case in examination
          if (
            action.name === PRODUCT_PERMISSIONS.EDIT_EXAMINATION_VALUES &&
            this.viewOption === "examination_overview"
          ) {
            return;
          }

          // to do: find a better way to define actions in product definitions or refactor all 'generic' actions handling
          // only show edit_medical_history on medical history panel (using customActionHandler.vue)
          if (action.name === PRODUCT_PERMISSIONS.EDIT_MEDICAL_HISTORY) {
            return;
          }

          /**
           * If UCR can be enabled, but not used, we add this action to show a dialog to use UCR.
           * If it is used, we don't add the action.
           */
          if (action.name === PRODUCT_ACTION_NAMES.UCR_PROMOTION) {
            if (
              !productHasPermission(
                PRODUCT_ACTION_NAMES.UCR_PROMOTION,
                this.product,
              ) ||
              this.$store.state.user.has_ucr
            ) {
              return;
            }
          }

          enabledActions.push({
            ...action,
            enabled: enabled,
            actionTitle:
              isCdrFlagCase && this.viewOption === "patient_overview"
                ? this.trans("admin.cdr.reportPatient")
                : this.getActionTitle(action),
          });
        }
      });

      this.availableExaminationActions = enabledActions;
    },
    getActionTitle: function (action) {
      if (action.hasBoolValue && this.examinationActions) {
        return this.examinationActions[action.key]
          ? this.trans(action.removeTitleKey)
          : this.trans(action.titleKey);
      }
      return this.trans(action.titleKey);
    },
    updateActionTitle: function (action) {
      this.availableExaminationActions.find((a) => {
        if (a.key === action.key) {
          a.actionTitle = this.getActionTitle(a);
        }
      });
    },
    onAction: function (item) {
      this.$emit("on-action", item);
      this.updateActionTitle(item);
    },
  },
};
</script>
